import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Avatar,
    Button,
    IconButton,
    Rating,
    Stack,
    useTheme,
    alpha,
    CircularProgress,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import {
    ArrowBackIosNew,
    ArrowForwardIos,
    FormatQuote,
    School,
    PlayArrow,
    Pause,
} from "@mui/icons-material";
import SwanLogo from "../../assets/logos/swansolutions_logo.png";

// Real testimonials from actual customers
const testimonials = [
    {
        name: "Emily Thornton",
        title: "Director of Child Nutrition",
        organization: "Thomasville City Schools",
        image: "https://api.figk-12.com/Files/WhatWeDoBoxes/Image/130d17b2-f8fe-4e84-ac3c-4bf0728bb5f8_20241213153031.png",
        quote: "FIG K-12 is a very efficient child nutrition professional development tool. I have found the FIG K-12 to be one of our school food service best purchases. The program streamlines the required school food service professional development requirements.",
        rating: 5,
    },
    {
        name: "Julie Fuerch",
        title: "Food Service Director",
        organization: "Barker Central & Royalton Hartland Central Schools",
        image: "https://api.figk-12.com/Files/WhatWeDoBoxes/Image/bad62c6a-3b22-402d-911d-5e1edf7b9927_20241213152144.png",
        quote: "FIG K12 has made it much easier to onboard, train, and keep everyone up to date. It's become a critical part of our department's success. Training is now a continuous process made simple.",
        rating: 5,
    },
    {
        name: "Quentin Cavanagh",
        title: "Nutrition Services Manager",
        organization: "Anderson School District 5",
        image: "https://api.figk-12.com/Files/WhatWeDoBoxes/Image/4f6030b1-3f1b-4bd8-94b2-ce4b4700c24b_20241213153236.png",
        quote: "On the back end it is very easy to set up Professional Development tasks and the reports are simple to access and understand. The support team has been responsive and open to suggestions for improvement.",
        rating: 5,
    },
    {
        name: "Scott Ziobrowski",
        title: "Food Service Director",
        organization: "Hilton Central School District",
        image: "https://api.figk-12.com/Files/WhatWeDoBoxes/Image/375f8a72-8783-4ce5-9af3-d0879d63b2f9_20241218115132.png",
        quote: "As a Food Service Director, it is important to streamline and utilize time management to the best of our abilities, and your software has allowed us to do that. The tracking of CEU's has allowed us to maximize our time.",
        rating: 5,
    },
];

export default function Testimonials() {
    const theme = useTheme();
    const [index, setIndex] = useState(0);
    const [direction, setDirection] = useState(0);
    const [isAutoPlaying, setIsAutoPlaying] = useState(true);
    const [isPaused, setIsPaused] = useState(false);
    const [districts, setDistricts] = useState([]);
    const [loadingDistricts, setLoadingDistricts] = useState(true);

    // Fetch districts from API
    useEffect(() => {
        const fetchDistricts = async () => {
            try {
                const res = await fetch("https://azuresql.swancreate.com/api/the/schooldistrict");
                const data = await res.json();
                // Filter for real districts shown on main site
                const filtered = data
                    .filter(d =>
                        d.IsShowAsClientOnMainSite === true &&
                        d.DistrictName &&
                        !d.DistrictName.toLowerCase().includes("test") &&
                        d.DistrictName !== "Swan Solutions" &&
                        d.DistrictName !== "NxtGen Network"
                    )
                    .map(d => d.DistrictName.trim());
                setDistricts(filtered);
            } catch (err) {
                console.error("Failed to fetch districts:", err);
                setDistricts([]);
            } finally {
                setLoadingDistricts(false);
            }
        };
        fetchDistricts();
    }, []);

    const handleNext = useCallback(() => {
        setDirection(1);
        setIndex((prev) => (prev + 1) % testimonials.length);
    }, []);

    const handlePrev = useCallback(() => {
        setDirection(-1);
        setIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    }, []);

    // Auto-advance carousel
    useEffect(() => {
        if (!isAutoPlaying || isPaused) return;
        const timer = setInterval(handleNext, 6000);
        return () => clearInterval(timer);
    }, [isAutoPlaying, isPaused, handleNext]);

    const t = testimonials[index];

    const glassCard = {
        background: alpha(theme.palette.common.white, 0.85),
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        border: `1px solid ${alpha(theme.palette.common.white, 0.4)}`,
        borderRadius: "20px",
        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.1)}`,
    };

    const cardVariants = {
        enter: (dir) => ({
            x: dir > 0 ? 300 : -300,
            opacity: 0,
        }),
        center: {
            x: 0,
            opacity: 1,
            transition: { duration: 0.4, ease: "easeOut" },
        },
        exit: (dir) => ({
            x: dir > 0 ? -300 : 300,
            opacity: 0,
            transition: { duration: 0.3, ease: "easeIn" },
        }),
    };

    return (
        <Box sx={{ minHeight: "100vh", bgcolor: theme.palette.background.default }}>

            {/* HERO */}
            <Box
                component={motion.section}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                sx={{
                    width: "100vw",
                    marginLeft: "calc(-50vw + 50%)",
                    py: { xs: 8, md: 12 },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    color: "#fff",
                    background: `linear-gradient(180deg, 
                        #5cc8ed 0%, 
                        #3a9fd4 30%, 
                        ${theme.palette.primary.main} 60%, 
                        ${theme.palette.primary.dark} 100%)`,
                }}
            >
                <Container maxWidth="md">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.15, duration: 0.4 }}
                    >
                        <Box
                            component="img"
                            src={SwanLogo}
                            alt="Swan Solutions"
                            sx={{ height: { xs: 60, md: 90 }, mb: 3 }}
                        />
                        <Typography
                            variant="h1"
                            sx={{
                                fontWeight: 900,
                                fontSize: { xs: "2.2rem", md: "3rem" },
                                letterSpacing: "-0.03em",
                                mb: 2,
                            }}
                        >
                            Real Impact, Real Voices
                        </Typography>
                        <Typography
                            variant="h6"
                            sx={{
                                color: "rgba(255,255,255,0.9)",
                                fontWeight: 400,
                                maxWidth: 600,
                                mx: "auto",
                            }}
                        >
                            Hear from school nutrition leaders across the country who are transforming
                            their operations with Swan Solutions and FIG K-12.
                        </Typography>
                    </motion.div>
                </Container>
            </Box>

            {/* DISTRICT MARQUEE */}
            <Box sx={{ py: 5, overflow: "hidden" }}>
                <Typography
                    variant="overline"
                    sx={{
                        display: "block",
                        textAlign: "center",
                        color: "text.secondary",
                        fontWeight: 700,
                        letterSpacing: 2,
                        mb: 3,
                    }}
                >
                    Trusted by school districts nationwide
                </Typography>

                {loadingDistricts ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : districts.length > 0 ? (
                    <Box
                        sx={{
                            position: "relative",
                            "&::before, &::after": {
                                content: '""',
                                position: "absolute",
                                top: 0,
                                bottom: 0,
                                width: 100,
                                zIndex: 2,
                                pointerEvents: "none",
                            },
                            "&::before": {
                                left: 0,
                                background: `linear-gradient(90deg, ${theme.palette.background.default}, transparent)`,
                            },
                            "&::after": {
                                right: 0,
                                background: `linear-gradient(90deg, transparent, ${theme.palette.background.default})`,
                            },
                        }}
                    >
                        <motion.div
                            animate={{ x: ["0%", "-50%"] }}
                            transition={{
                                x: { repeat: Infinity, repeatType: "loop", duration: 300, ease: "linear" },
                            }}
                            style={{ display: "flex", gap: 16, width: "fit-content" }}
                        >
                            {[...districts, ...districts].map((name, i) => (
                                <Box
                                    key={i}
                                    sx={{
                                        ...glassCard,
                                        px: 2.5,
                                        py: 1.2,
                                        borderRadius: 99,
                                        whiteSpace: "nowrap",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                    }}
                                >
                                    <School sx={{ fontSize: 18, color: theme.palette.secondary.main }} />
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                                        {name}
                                    </Typography>
                                </Box>
                            ))}
                        </motion.div>
                    </Box>
                ) : null}
            </Box>

            {/* TESTIMONIALS CAROUSEL */}
            <Container maxWidth="md" sx={{ py: { xs: 4, md: 8 } }}>
                <Typography
                    variant="h3"
                    sx={{
                        fontWeight: 800,
                        textAlign: "center",
                        mb: 5,
                        color: theme.palette.primary.dark,
                    }}
                >
                    What Our Partners Say
                </Typography>

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: { xs: 1, md: 3 },
                    }}
                    onMouseEnter={() => setIsPaused(true)}
                    onMouseLeave={() => setIsPaused(false)}
                >
                    {/* Prev Button */}
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                        <IconButton
                            onClick={handlePrev}
                            sx={{
                                ...glassCard,
                                width: { xs: 44, md: 56 },
                                height: { xs: 44, md: 56 },
                                color: theme.palette.primary.main,
                            }}
                        >
                            <ArrowBackIosNew />
                        </IconButton>
                    </motion.div>

                    {/* Testimonial Card */}
                    <Box
                        sx={{
                            flex: 1,
                            maxWidth: 700,
                            minHeight: { xs: 420, md: 380 },
                            position: "relative",
                        }}
                    >
                        <AnimatePresence initial={false} custom={direction} mode="wait">
                            <motion.div
                                key={index}
                                custom={direction}
                                variants={cardVariants}
                                initial="enter"
                                animate="center"
                                exit="exit"
                                style={{ position: "absolute", width: "100%" }}
                            >
                                <Card sx={{ ...glassCard, p: { xs: 3, md: 4 } }}>
                                    <CardContent
                                        sx={{
                                            display: "flex",
                                            flexDirection: { xs: "column", md: "row" },
                                            alignItems: { xs: "center", md: "flex-start" },
                                            gap: 3,
                                            p: 0,
                                        }}
                                    >
                                        {/* Avatar */}
                                        <Box sx={{ textAlign: "center", flexShrink: 0 }}>
                                            <Avatar
                                                src={t.image}
                                                alt={t.name}
                                                sx={{
                                                    width: { xs: 100, md: 120 },
                                                    height: { xs: 100, md: 120 },
                                                    border: `3px solid ${theme.palette.secondary.main}`,
                                                    boxShadow: `0 4px 20px ${alpha(theme.palette.secondary.main, 0.3)}`,
                                                }}
                                            />
                                            <Rating value={t.rating} readOnly sx={{ mt: 1.5 }} />
                                        </Box>

                                        {/* Content */}
                                        <Box sx={{ flex: 1, textAlign: { xs: "center", md: "left" } }}>
                                            <FormatQuote
                                                sx={{
                                                    fontSize: 40,
                                                    color: alpha(theme.palette.secondary.main, 0.3),
                                                    transform: "scaleX(-1)",
                                                    display: { xs: "none", md: "block" },
                                                    mb: -1,
                                                }}
                                            />
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    fontStyle: "italic",
                                                    lineHeight: 1.8,
                                                    color: theme.palette.text.primary,
                                                    mb: 2.5,
                                                }}
                                            >
                                                "{t.quote}"
                                            </Typography>
                                            <Box sx={{ borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`, pt: 2 }}>
                                                <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                                                    {t.name}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: theme.palette.secondary.dark, fontWeight: 600 }}>
                                                    {t.title}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: theme.palette.text.secondary,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: { xs: "center", md: "flex-start" },
                                                        gap: 0.5,
                                                        mt: 0.5,
                                                    }}
                                                >
                                                    <School sx={{ fontSize: 14 }} />
                                                    {t.organization}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        </AnimatePresence>
                    </Box>

                    {/* Next Button */}
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                        <IconButton
                            onClick={handleNext}
                            sx={{
                                ...glassCard,
                                width: { xs: 44, md: 56 },
                                height: { xs: 44, md: 56 },
                                color: theme.palette.primary.main,
                            }}
                        >
                            <ArrowForwardIos />
                        </IconButton>
                    </motion.div>
                </Box>

                {/* Controls */}
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ mt: 4 }}>
                    <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                        <IconButton
                            onClick={() => setIsAutoPlaying(!isAutoPlaying)}
                            sx={{
                                ...glassCard,
                                width: 40,
                                height: 40,
                                color: isAutoPlaying ? theme.palette.secondary.main : theme.palette.text.secondary,
                            }}
                        >
                            {isAutoPlaying ? <Pause /> : <PlayArrow />}
                        </IconButton>
                    </motion.div>

                    <Stack direction="row" spacing={1}>
                        {testimonials.map((_, i) => (
                            <Box
                                key={i}
                                component="button"
                                onClick={() => {
                                    setDirection(i > index ? 1 : -1);
                                    setIndex(i);
                                }}
                                sx={{
                                    width: i === index ? 28 : 10,
                                    height: 10,
                                    borderRadius: 99,
                                    border: "none",
                                    cursor: "pointer",
                                    background: i === index
                                        ? `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.primary.light})`
                                        : alpha(theme.palette.primary.main, 0.2),
                                    transition: "all 0.3s ease",
                                    "&:hover": {
                                        background: i === index
                                            ? `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.primary.light})`
                                            : alpha(theme.palette.primary.main, 0.35),
                                    },
                                }}
                            />
                        ))}
                    </Stack>
                </Stack>
            </Container>

            {/* CTA */}
            <Container maxWidth="md" sx={{ pb: { xs: 8, md: 12 } }}>
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                >
                    <Card
                        sx={{
                            ...glassCard,
                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                            p: { xs: 4, md: 6 },
                            textAlign: "center",
                        }}
                    >
                        <Typography variant="h4" sx={{ fontWeight: 800, color: "#fff", mb: 2 }}>
                            Ready to Transform Your District?
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: "rgba(255,255,255,0.85)",
                                maxWidth: 500,
                                mx: "auto",
                                mb: 4,
                            }}
                        >
                            Join school districts across the country already saving time and
                            improving compliance with Swan Solutions.
                        </Typography>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center">
                            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                                <Button
                                    href="/contact"
                                    variant="contained"
                                    size="large"
                                    sx={{
                                        background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.light})`,
                                        color: theme.palette.primary.dark,
                                        fontWeight: 700,
                                        px: 4,
                                        py: 1.5,
                                        borderRadius: "12px",
                                        boxShadow: `0 4px 20px ${alpha(theme.palette.secondary.main, 0.4)}`,
                                    }}
                                >
                                    Schedule a Demo
                                </Button>
                            </motion.div>
                            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                                <Button
                                    href="/solutions"
                                    variant="outlined"
                                    size="large"
                                    sx={{
                                        borderColor: "rgba(255,255,255,0.5)",
                                        color: "#fff",
                                        fontWeight: 600,
                                        px: 4,
                                        py: 1.5,
                                        borderRadius: "12px",
                                        "&:hover": {
                                            borderColor: "#fff",
                                            background: "rgba(255,255,255,0.1)",
                                        },
                                    }}
                                >
                                    Learn More
                                </Button>
                            </motion.div>
                        </Stack>
                    </Card>
                </motion.div>
            </Container>
        </Box>
    );
}